// Telemetry initializes first (side-effect import) so App Insights is live —
// including auto route tracking and unhandled-rejection capture — before
// anything renders. No-ops when the connection string isn't configured.
import './telemetry'
import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { App } from './app/App'
import './app/globals.css'

// Leftover service-worker cleanup. The pre-React (Angular/OneSignal) site registered
// service workers that persist on devices and keep serving the dead old app. We ship
// NO service worker of our own, so if this code is running the new app has loaded and
// any registration present is stale — unregister every one and drop its caches so it
// can never intercept a future load. This complements the server-side self-destruct
// workers (which reach devices still fully stuck on the old bundle); here we clean up
// any device that has already reached the new app. Best-effort; ignore failures.
if ('serviceWorker' in navigator) {
  navigator.serviceWorker
    .getRegistrations()
    .then((regs) => regs.forEach((r) => void r.unregister()))
    .catch(() => {})
  if (typeof caches !== 'undefined') {
    caches
      .keys()
      .then((keys) => keys.forEach((k) => void caches.delete(k)))
      .catch(() => {})
  }
}

// Stale-tab recovery. After a deploy, Vite's content-hashed chunks are renamed
// and the old files are removed (the React deploy is clean). A browser tab still
// running the pre-deploy bundle 404s the moment it lazy-loads a now-missing
// chunk (route navigation, opening a dropdown, etc.). Reload once to fetch the
// fresh index.html + current chunk names. A sessionStorage flag guards against
// an infinite reload loop when the failure is a genuinely broken deploy rather
// than a stale tab; it's cleared on a successful load so a later deploy in a
// long-lived tab can recover again.
const RELOAD_FLAG = 'csfbl:chunk-reload'

function reloadOnceForStaleChunks() {
  try {
    if (sessionStorage.getItem(RELOAD_FLAG)) return
    sessionStorage.setItem(RELOAD_FLAG, '1')
  } catch {
    // sessionStorage unavailable (private mode / blocked) — reload anyway once.
  }
  window.location.reload()
}

// Vite dispatches this when a dynamically-imported (lazy route) chunk fails.
window.addEventListener('vite:preloadError', ((event: Event) => {
  event.preventDefault() // stop Vite from rethrowing as an unhandled error
  reloadOnceForStaleChunks()
}) as EventListener)

// Belt-and-suspenders: some chunk failures surface as an unhandled rejection.
window.addEventListener('unhandledrejection', (event) => {
  const reason = event.reason as { name?: string; message?: string } | undefined
  const name = reason?.name ?? ''
  const message = reason?.message ?? ''
  if (
    name === 'ChunkLoadError' ||
    /failed to fetch dynamically imported module|importing a module script failed/i.test(message)
  ) {
    reloadOnceForStaleChunks()
  }
})

// Clear the guard after a clean load so a future stale-deploy can recover.
window.addEventListener('load', () => {
  try {
    sessionStorage.removeItem(RELOAD_FLAG)
  } catch {
    // ignore
  }
})

// Build stamp — confirm which commit is live by glancing at the DevTools console
// (or `curl -s <site>/ | grep app-version`). The <meta> is injected into index.html
// at build time by vite.config's versionMetaPlugin; reading it here keeps a single
// source of truth (no separately-injected global to drift).
const buildMeta = document
  .querySelector('meta[name="app-version"]')
  ?.getAttribute('content')
if (buildMeta) {
  console.info(
    `%cBaseHit build %c${buildMeta}`,
    'color:#5F8FC5;font-weight:bold',
    'color:#113a57',
  )
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <App />
  </StrictMode>,
)
